/*
 * Two-column Rewards spreadsheet parser. Accepts CSV and XLSX/XLS.
 * Column 1 = grade, column 2 = teacher. Rows are grouped into grades
 * (first-seen order), with teachers de-duped within each grade
 * (case-insensitive). Rows missing a grade or teacher are reported so
 * the teacher can see what we skipped before importing.
 *
 * Mirrors the birthday importer (`birthdayParse.ts`) — same header
 * detection, same tiny CSV parser, same read-excel-file browser path.
 */

import { readSheet } from 'read-excel-file/browser';

export interface ParsedRewardsGrade {
  name: string;
  teachers: string[];
}

export interface RewardsParseError {
  /** 1-based row in the source file, for the preview UI. */
  row: number;
  raw: string;
  reason: string;
}

export interface RewardsParseResult {
  grades: ParsedRewardsGrade[];
  /** Total teachers across all grades (for the summary line). */
  teacherCount: number;
  errors: RewardsParseError[];
}

const HEADER_KEYWORDS = new Set([
  'grade',
  'grade level',
  'grades',
  'level',
  'teacher',
  'teacher name',
  'teachers',
  'class',
  'classroom',
  'name',
  'room',
]);

function looksLikeHeader(row: ReadonlyArray<unknown>): boolean {
  if (row.length === 0) return false;
  const cells = row
    .slice(0, 2)
    .map((c) => (typeof c === 'string' ? c.trim().toLowerCase() : null))
    .filter((c): c is string => !!c);
  return cells.some((c) => HEADER_KEYWORDS.has(c));
}

function cellToString(value: unknown): string {
  if (value === null || value === undefined) return '';
  return typeof value === 'string' ? value.trim() : String(value).trim();
}

/**
 * Group parsed rows into grades. Exported for unit testing — the file
 * readers (xlsx + csv) both funnel their rows through here.
 */
export function rowsToRewards(rows: ReadonlyArray<ReadonlyArray<unknown>>): RewardsParseResult {
  const grades: ParsedRewardsGrade[] = [];
  const errors: RewardsParseError[] = [];
  // grade key (lowercased) -> its entry in `grades`
  const byGrade = new Map<string, ParsedRewardsGrade>();
  // grade key -> set of teacher keys already seen (case-insensitive)
  const seenTeachers = new Map<string, Set<string>>();

  const startIndex = rows.length > 0 && looksLikeHeader(rows[0]!) ? 1 : 0;

  for (let i = startIndex; i < rows.length; i++) {
    const row = rows[i]!;
    const rowNumber = i + 1; // 1-based for the user

    const gradeName = cellToString(row[0]);
    const teacherName = cellToString(row[1]);

    // Fully empty row — skip silently.
    if (!gradeName && !teacherName) continue;

    if (!gradeName) {
      errors.push({ row: rowNumber, raw: `, ${teacherName}`, reason: 'Missing grade' });
      continue;
    }
    if (!teacherName) {
      errors.push({ row: rowNumber, raw: `${gradeName}, `, reason: 'Missing teacher' });
      continue;
    }

    const gradeKey = gradeName.toLowerCase();
    let entry = byGrade.get(gradeKey);
    if (!entry) {
      entry = { name: gradeName, teachers: [] };
      byGrade.set(gradeKey, entry);
      seenTeachers.set(gradeKey, new Set());
      grades.push(entry);
    }

    const teacherKey = teacherName.toLowerCase();
    const seen = seenTeachers.get(gradeKey)!;
    if (seen.has(teacherKey)) continue; // duplicate teacher within a grade — skip silently
    seen.add(teacherKey);
    entry.teachers.push(teacherName);
  }

  const teacherCount = grades.reduce((n, g) => n + g.teachers.length, 0);
  return { grades, teacherCount, errors };
}

// ---------- CSV ----------

/** Tiny CSV parser. Handles quoted fields containing commas/newlines.
 *  Good enough for the shapes a teacher's spreadsheet export produces.
 *  (Same parser as the birthday importer.) */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n') {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else if (ch === '\r') {
      if (text[i + 1] !== '\n') {
        row.push(cell);
        rows.push(row);
        row = [];
        cell = '';
      }
    } else {
      cell += ch;
    }
  }
  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  while (rows.length > 0 && rows[rows.length - 1]!.every((c) => c.trim() === '')) {
    rows.pop();
  }
  return rows;
}

// ---------- Public API ----------

export async function parseRewardsFile(file: File): Promise<RewardsParseResult> {
  const lower = file.name.toLowerCase();
  if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
    const rows = await readSheet(file);
    return rowsToRewards(rows as ReadonlyArray<ReadonlyArray<unknown>>);
  }
  // Default: treat as CSV (covers .csv, .tsv, and anything plain-text).
  const text = await file.text();
  const rows = parseCsv(text);
  return rowsToRewards(rows);
}
